import { CLICK_IDS, UTM_KEY } from "@/constants";
import { search } from "@/constants/location-search";
import { referrer } from "@/constants/referrer";
import { storage } from "@/lib/storage";

const SESSION_MINUTES = 30;
const SEVEN_DAYS_MINUTES = 7 * 24 * 60;

interface UTM {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

// Storage key == param name (in uppercase). CLICK_IDS is the single source of truth.
// To add a new click ID integration: add the param to CLICK_IDS in constants/index.ts.

export const extractDomainKeyword = (url: string) => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const parts = domain.split(".");
    return parts.length > 2 ? parts[1] : parts[0];
  } catch (error) {
    console.error("invalid Url:", error);
    return null;
  }
};

const getIdentifiersParameters = (): {
  utms: UTM;
  [param: string]: string | undefined | UTM;
} => {
  const emptyClickIds: Record<string, string | undefined> = Object.fromEntries(
    CLICK_IDS.map((id) => [id, undefined]),
  );
  const emptyUtms: UTM = {
    source: "",
    medium: "",
    campaign: "",
    term: "",
    content: "",
  };

  try {
    const urlParams = new URLSearchParams(search);

    const clickIds: Record<string, string | undefined> = Object.fromEntries(
      CLICK_IDS.map((id) => [id, urlParams.get(id) ?? undefined]),
    );

    const utms: UTM = {
      source:
        urlParams.get("utm_source") ||
        urlParams.get("source") ||
        extractDomainKeyword(referrer) ||
        undefined,
      medium:
        urlParams.get("utm_medium") || urlParams.get("medium") || undefined,
      campaign:
        urlParams.get("utm_campaign") || urlParams.get("campaign") || undefined,
      term: urlParams.get("utm_term") || urlParams.get("term") || undefined,
      content:
        urlParams.get("utm_content") || urlParams.get("content") || undefined,
    };

    return { utms, ...clickIds };
  } catch (error) {
    console.log(error);
    return { utms: emptyUtms, ...emptyClickIds };
  }
};

export const storeIdentifiers = async () => {
  const { utms, ...clickIds } = getIdentifiersParameters();

  await Promise.all(
    CLICK_IDS.filter((param) => clickIds[param]).map((param) =>
      storage.setItemWithExpiry(
        param.toUpperCase(),
        clickIds[param],
        SEVEN_DAYS_MINUTES,
      ),
    ),
  );

  if (utms.source && utms.medium) {
    await storage.setItemWithExpiry(UTM_KEY, utms, SESSION_MINUTES);
  }
};

export const readClickIdsFromStorage = async () => {
  const entries = await Promise.all(
    CLICK_IDS.map(async (param) => [
      param,
      (await storage.getItemWithExpiry<string | undefined>(
        param.toUpperCase(),
      )) || "",
    ]),
  );
  return Object.fromEntries(entries) as Record<string, string>;
};

export const getIdentifiersFromStorage = async () => {
  const clickIds = await readClickIdsFromStorage();
  const utms = await storage.getItemWithExpiry<UTM | null>(UTM_KEY);

  return {
    ...clickIds,
    utm_source: utms?.source || "",
    utm_medium: utms?.medium || "",
    utm_campaign: utms?.campaign || "",
    utm_term: utms?.term || "",
    utm_content: utms?.content || "",
  };
};

export async function getAdsProporties() {
  try {
    const clickIds = await readClickIdsFromStorage();
    const utms = await storage.getItemWithExpiry<UTM>(UTM_KEY);

    const properties: Record<string, string> = {};

    for (const [param, value] of Object.entries(clickIds)) {
      if (value) properties[param] = value;
    }

    if (utms?.source) properties.source = utms.source;
    if (utms?.medium) properties.medium = utms.medium;

    return properties;
  } catch (e) {
    console.error(e);
    return {};
  }
}

export function getGALinkerParam(): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      resolve(null);
      return;
    }

    const ga4Ids = Object.keys(window.google_tag_manager || []).filter((e) =>
      e.startsWith("G-"),
    );

    if (ga4Ids.length === 0) {
      resolve(null);
      return;
    }

    window.gtag("get", ga4Ids[0], "linker_param", (linkerParam: string) => {
      resolve(linkerParam);
    });
  });
}
